import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Flex, Text, VStack, Divider, Input, InputGroup, InputLeftElement, Select, Icon, Badge, Skeleton,
    Alert, AlertIcon, AlertDescription, Button, Collapse, HStack,
} from '@chakra-ui/react';
import { FaSearch, FaHistory, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import RodapePaginacao from './RodapePaginacao';
import { getEventosComissao } from '../../../services/api';
import { formatarDataHora, mensagemErro } from '../formato';

const POR_PAGINA = 20;
const COR_ACAO = {
    pagamento_registrado: 'green',
    pagamento_desfeito: 'red',
    item_desfeito: 'red',
    rastreio_alterado: 'gray',
    site_confirmado: 'purple',
    site_recusado: 'orange',
    site_reenviado: 'purple',
};
const ROTULO_ACAO = {
    pagamento_registrado: 'Pagamento',
    pagamento_desfeito: 'Desfeito',
    item_desfeito: 'Desfeito',
    rastreio_alterado: 'Status',
    site_confirmado: 'Site',
    site_recusado: 'Site',
    site_reenviado: 'Site',
};

// Historico de acoes do vendedor (quem fez o que e quando), paginado na API.
const HistoricoEventos = ({ userId, versao }) => {
    const [aberto, setAberto] = useState(false);
    const [grupo, setGrupo] = useState('');
    const [busca, setBusca] = useState('');
    const [buscaFinal, setBuscaFinal] = useState('');
    const [pagina, setPagina] = useState(1);
    const [dados, setDados] = useState(null);
    const [erro, setErro] = useState(null);

    // Busca so consulta depois de uma pausa; filtro novo volta para a pagina 1.
    useEffect(() => {
        const b = busca.trim();
        if (b === buscaFinal) return undefined;
        const t = setTimeout(() => { setBuscaFinal(b); setPagina(1); }, 400);
        return () => clearTimeout(t);
    }, [busca, buscaFinal]);

    const carregar = useCallback(async () => {
        if (!userId || !aberto) return;
        setErro(null);
        try {
            const params = { pagina, por_pagina: POR_PAGINA };
            if (grupo) params.grupo = grupo;
            if (buscaFinal) params.busca = buscaFinal;
            setDados(await getEventosComissao(userId, params));
        } catch (e) {
            setErro(mensagemErro(e, 'Não foi possível carregar o histórico.'));
        }
    }, [userId, aberto, pagina, grupo, buscaFinal]);

    useEffect(() => { carregar(); }, [carregar, versao]);

    return (
        <Box bg="white" borderRadius="xl" borderWidth="1px" borderColor="gray.200" boxShadow="sm" overflow="hidden" mb={5}>
            <Flex px={{ base: 3, md: 4 }} py={3} align="center" justify="space-between" gap={3}>
                <HStack spacing={2}>
                    <Icon as={FaHistory} color="gray.500" />
                    <Text fontWeight="semibold" color="gray.800" fontSize="sm">Histórico de ações</Text>
                </HStack>
                <Button size="xs" variant="ghost" rightIcon={<Icon as={aberto ? FaChevronUp : FaChevronDown} />} onClick={() => setAberto(!aberto)} aria-expanded={aberto}>
                    {aberto ? 'Ocultar' : 'Mostrar'}
                </Button>
            </Flex>
            <Collapse in={aberto} animateOpacity>
                <Flex px={{ base: 3, md: 4 }} py={3} gap={2} bg="gray.50" borderTopWidth="1px" borderBottomWidth="1px" borderColor="gray.100" flexWrap="wrap">
                    <InputGroup size="sm" flex="1 1 220px">
                        <InputLeftElement pointerEvents="none"><Icon as={FaSearch} color="gray.400" /></InputLeftElement>
                        <Input bg="white" borderRadius="md" placeholder="Buscar no histórico (venda, motivo, pessoa)" value={busca} onChange={(e) => setBusca(e.target.value)} aria-label="Buscar no histórico" />
                    </InputGroup>
                    <Select size="sm" bg="white" borderRadius="md" w={{ base: '100%', sm: '200px' }} value={grupo} onChange={(e) => { setGrupo(e.target.value); setPagina(1); }} aria-label="Tipo de ação">
                        <option value="">Todas as ações</option>
                        <option value="pagamento">Pagamentos</option>
                        <option value="desfazer">Desfeitos</option>
                        <option value="rastreio">Mudança de status</option>
                        <option value="site">Sistema de Parceiros</option>
                    </Select>
                </Flex>
                {erro ? (
                    <Alert status="error" m={4} w="auto" borderRadius="md" fontSize="sm" flexWrap="wrap" gap={2}>
                        <AlertIcon />
                        <AlertDescription flex="1">{erro}</AlertDescription>
                        <Button size="xs" onClick={carregar}>Tentar de novo</Button>
                    </Alert>
                ) : !dados ? (
                    <VStack p={4} spacing={2}>{[0, 1, 2].map((i) => <Skeleton key={i} h="28px" w="100%" />)}</VStack>
                ) : dados.eventos.length === 0 ? (
                    <Text fontSize="sm" color="gray.500" textAlign="center" py={6}>Nenhuma ação encontrada.</Text>
                ) : (
                    <VStack align="stretch" spacing={0} divider={<Divider />}>
                        {dados.eventos.map((e) => (
                            <Flex key={e.id} px={{ base: 3, md: 4 }} py={2.5} gap={{ base: 1, md: 3 }} direction={{ base: 'column', md: 'row' }} align={{ md: 'center' }}>
                                <HStack spacing={2} minW={{ md: '210px' }} flexShrink={0}>
                                    <Badge colorScheme={COR_ACAO[e.acao] || 'gray'} textTransform="none" borderRadius="full" px={2} fontSize="10px">{ROTULO_ACAO[e.acao] || e.acao}</Badge>
                                    <Text fontSize="xs" color="gray.500">{formatarDataHora(e.criado_em)}</Text>
                                </HStack>
                                <Text fontSize="sm" color="gray.700" flex="1">{e.detalhe}</Text>
                                <Text fontSize="xs" color="gray.500" flexShrink={0}>{e.autor}</Text>
                            </Flex>
                        ))}
                    </VStack>
                )}
                {dados && (
                    <RodapePaginacao pagina={dados.paginacao.pagina} paginas={dados.paginacao.paginas} total={dados.paginacao.total} porPagina={dados.paginacao.por_pagina} onMudar={setPagina} rotulo="ações" />
                )}
            </Collapse>
        </Box>
    );
};

export default HistoricoEventos;
